import { hasGoogleAnalyticsConsent } from "@seamapi/shared/google-measurement-consent"
import { hasAdvertisingConsent } from "@seamapi/shared/openai-ads"

export type AccountSignup = {
  event_id: string
  account_type: "self_serve" | "invited"
}

const PREFIX = "seam:google-account-signup:"
const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const MAX_PENDING_AGE = 24 * 60 * 60 * 1000

type Receipt = AccountSignup & {
  destination: string
  created_at: number
  dispatched: boolean
}

type Runtime = {
  destination: string | undefined
  storage: () => Storage
  gtag: () => ((...args: unknown[]) => void) | undefined
  now: () => number
  isReady: () => boolean
  hasConsent: () => boolean
  isSafePage?: () => boolean
}

// Validate a destination. The browser may report to GA4 analytics and the
// separately verified native Ads action; each keeps its own dispatch marker.
// An explicitly invalid override disables sending instead of silently routing
// the conversion somewhere else. A bare Ads base id is not a conversion action.
export const signupDestination = (override?: string, ga4Id?: string) => {
  const value = override ?? ga4Id
  return value && /^(G-[A-Z0-9]+|AW-[0-9]+\/[A-Za-z0-9_-]+)$/i.test(value)
    ? value
    : undefined
}

export const createSignupTracker = (runtime: Runtime) => {
  const memory = new Map<string, Receipt>()
  const remove = (key: string) => {
    memory.delete(key)
    try {
      runtime.storage().removeItem(key)
    } catch {
      /* Storage may be blocked. */
    }
  }
  const expired = (receipt: Receipt) =>
    !Number.isFinite(receipt.created_at) ||
    runtime.now() - receipt.created_at > MAX_PENDING_AGE ||
    runtime.now() < receipt.created_at
  const read = (key: string) => {
    // A successful dispatch must stay deduplicated this page even if persisting
    // the acknowledgement fails after the pending write succeeded.
    const cached = memory.get(key)
    if (cached && expired(cached)) {
      remove(key)
      return undefined
    }
    if (cached?.dispatched) return cached
    try {
      const stored: Receipt | null = JSON.parse(
        runtime.storage().getItem(key) ?? "null"
      )
      if (stored && expired(stored)) {
        remove(key)
        return undefined
      }
      if (stored) return stored
    } catch {
      // Storage can be blocked. In-memory deduplication still works this page.
    }
    return memory.get(key)
  }
  const write = (key: string, receipt: Receipt) => {
    memory.set(key, receipt)
    try {
      runtime.storage().setItem(key, JSON.stringify(receipt))
    } catch {
      // Analytics must never fail login or invitation acceptance.
    }
  }

  const hasConsent = () => {
    try {
      return runtime.hasConsent() === true
    } catch {
      return false
    }
  }
  const discard = () => {
    memory.clear()
    try {
      const storage = runtime.storage()
      const keys: string[] = []
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i)
        if (key?.startsWith(PREFIX + runtime.destination + ":")) keys.push(key)
      }
      for (const key of keys) storage.removeItem(key)
    } catch {
      // Consent revocation must still stop dispatch when storage is blocked.
    }
  }

  const flush = () => {
    if (!hasConsent()) {
      discard()
      return
    }
    const gtag = runtime.gtag()
    const canDispatch =
      runtime.destination &&
      gtag &&
      runtime.isReady() &&
      runtime.isSafePage?.() !== false
    const keys = new Set(memory.keys())
    try {
      const storage = runtime.storage()
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i)
        if (key?.startsWith(PREFIX)) keys.add(key)
      }
    } catch {
      // Storage enumeration may be blocked; use this page's in-memory receipts.
    }
    for (const key of keys) {
      const receipt = read(key)
      if (
        !receipt ||
        !gtag ||
        !canDispatch ||
        receipt.dispatched ||
        receipt.destination !== runtime.destination ||
        !UUID.test(receipt.event_id) ||
        !["self_serve", "invited"].includes(receipt.account_type) ||
        !Number.isFinite(receipt.created_at) ||
        runtime.now() - receipt.created_at > MAX_PENDING_AGE
      )
        continue
      try {
        const isAds = receipt.destination.startsWith("AW-")
        // Use the existing gtag without asserting or granting consent. Never
        // initialize a tag, set user_data/user_id, or send the API response wholesale.
        gtag("event", isAds ? "conversion" : "sign_up", {
          send_to: receipt.destination,
          // Auth/invitation URLs can contain email addresses or bearer tokens.
          // Use canonical metadata, never the current URL/query or referrer.
          page_location: "https://console.seam.co/signup",
          page_referrer: "",
          ...(isAds
            ? { transaction_id: receipt.event_id }
            : { event_id: receipt.event_id }),
          account_type: receipt.account_type,
        })
        // Dispatched means accepted by gtag, NOT a delivery acknowledgement.
        write(key, { ...receipt, dispatched: true })
      } catch {
        // A missing/throwing tag keeps the receipt pending for a later flush.
      }
    }
  }

  const record = (signup: AccountSignup | null | undefined) => {
    if (
      !hasConsent() ||
      !runtime.destination ||
      !signup ||
      !UUID.test(signup.event_id) ||
      !["self_serve", "invited"].includes(signup.account_type)
    )
      return
    const key = PREFIX + runtime.destination + ":" + signup.event_id
    if (!read(key))
      write(key, {
        event_id: signup.event_id,
        account_type: signup.account_type,
        destination: runtime.destination,
        created_at: runtime.now(),
        dispatched: false,
      })
    flush()
  }
  return { record, flush }
}

let googleTagReady = false
let trackers: ReturnType<typeof createSignupTracker>[] | undefined
const getTrackers = () => {
  if (
    typeof window === "undefined" ||
    process.env.NODE_ENV !== "production" ||
    window.location.hostname !== "console.seam.co"
  )
    return []
  if (trackers) return trackers
  const ga4 = signupDestination(undefined, process.env.NEXT_PUBLIC_GA4_ID)
  const ads = signupDestination(
    process.env.NEXT_PUBLIC_GOOGLE_ADS_SIGNUP_SEND_TO
  )
  // GA4 remains an analytics event. Ads requires a verified native action;
  // a bare base id or a GA4 id in the Ads-only variable cannot enable it.
  const destinations = [ga4, ads?.startsWith("AW-") ? ads : undefined]
  trackers = destinations
    .filter((destination): destination is string => Boolean(destination))
    .map((destination) =>
      createSignupTracker({
        destination,
        storage: () => window.localStorage,
        gtag: () => window.gtag,
        now: Date.now,
        isReady: () => googleTagReady,
        hasConsent: destination.startsWith("AW-")
          ? hasAdvertisingConsent
          : hasGoogleAnalyticsConsent,
        // Google Ads can collect the real URL independently of page_location.
        // Keep receipts pending while auth/invite query strings or hashes exist.
        isSafePage: () =>
          window.location.search === "" && window.location.hash === "",
      })
    )
  return trackers
}

export const recordAccountSignup = (
  signup: AccountSignup | null | undefined
) => {
  for (const tracker of getTrackers()) tracker.record(signup)
}

export const flushAccountSignups = () => {
  for (const tracker of getTrackers()) tracker.flush()
}

// Called by Next Script only after the existing Google library loads. Merely
// finding the inline gtag queue is insufficient: refresh would lose that queue.
export const markSignupTagReady = () => {
  googleTagReady = true
  flushAccountSignups()
}

export const mountAccountSignupTracking = () => {
  flushAccountSignups()
  const interval = window.setInterval(flushAccountSignups, 1000)
  window.addEventListener("storage", flushAccountSignups)
  return () => {
    window.clearInterval(interval)
    window.removeEventListener("storage", flushAccountSignups)
  }
}
